import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Clock, Link2 } from 'lucide-react';

const AutomationCard = ({ automation, onDelete, onToggle, onEditLink }) => {
  const { id, name, description, active, lastTriggered, conditions = [], actions = [], template } = automation;
  const isLink = template?.type === 'linkedSwitches';
  const memberCount = template?.config?.members?.length ?? 0;

  const formatLastTriggered = () => {
    if (!lastTriggered) return 'Never';
    const date = new Date(lastTriggered);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getTriggerSummary = () => {
    if (conditions.length === 0) return 'No trigger set';
    if (conditions.length === 1) {
      const c = conditions[0];
      if (c.type === 'device') return `When ${c.attribute} ${c.operator} ${c.value}`;
      if (c.type === 'time') return c.operator === 'between' ? `Between ${c.start} - ${c.end}` : `At ${c.value}`;
      if (c.type === 'day') return `On ${Array.isArray(c.value) ? c.value.join(', ') : c.value}`;
    }
    return `When ${conditions.length} conditions met`;
  };

  const getActionsSummary = () => {
    if (actions.length === 0) return 'No actions';
    if (actions.length === 1) {
      const a = actions[0];
      if (a.type === 'device') return 'Control 1 device';
      if (a.type === 'scene') return 'Activate scene';
      if (a.type === 'notification') return 'Show notification';
    }
    return `Then ${actions.length} actions`;
  };

  return (
    <Card className={`
      transition-all duration-200 hover:scale-[0.98] active:scale-[0.96]
      ${active ? 'border-border/60 shadow-sm' : 'border-border/40 opacity-80'}
    `}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base font-medium truncate">{name}</CardTitle>
            {description && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{description}</p>
            )}
          </div>
          <Switch
            checked={active}
            onCheckedChange={() => onToggle && onToggle(id)}
          />
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        {isLink ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link2 className="h-4 w-4" />
            <span>{memberCount} switch{memberCount === 1 ? '' : 'es'} linked</span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm">
              <span className="text-muted-foreground">When</span>
              <span className="ml-2 font-normal">{getTriggerSummary()}</span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Then</span>
              <span className="ml-2 font-normal">{getActionsSummary()}</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{formatLastTriggered()}</span>
          </div>
          <div className="flex items-center gap-1">
            {isLink ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  onEditLink && onEditLink(automation);
                }}
              >
                Edit
              </Button>
            ) : (
              <Link to={`/automations/${id}`}>
                <Button variant="ghost" size="sm" className="h-8 px-3 text-xs">
                  Details
                </Button>
              </Link>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                onDelete && onDelete(id);
              }}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
              title="Delete automation"
            >
              ×
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AutomationCard;